// ITEM 12 — HER DMs LANE. Lala answered the simple ones; everything else is here with a draft.
//
// **PROPS IN, TREE OUT, NO HOOK** — deliberately, and it is the R27 lesson applied rather than
// re-learned. The rewrite box needs state, so the state lives in the PARENT and arrives as
// `draftText` + `onDraftText`. That keeps this component callable by `scripts/lr15-inbox-answer.mjs`,
// which is the only way an assertion about what her seat actually RENDERS can be cheap enough to run
// on every build. A capability behind a hook is a capability no gate can reach.
import type { ReactNode } from "react";
import { Send, X } from "lucide-react";
import { answerClassLabel, refusalSentence, EMPTY_INBOX_LANE, NO_DRAFT_NOTE, SENT_MARK, AUTO_REPLY_MAX_CHARS } from "../inboxAnswer.js";
import { MemberDoor } from "./MemberDoor";

export type InboxRow = {
  id: string; threadId: string; messageId: string; memberId: string; memberName: string;
  cls: string; by: string; why: string; draft: string | null; refusals: string[];
  message: string; at: string; sentAt?: string | null; sentBody?: string | null; ownerAction?: string | null;
};
export type InboxAction = { key: string; label: string; note: string };

const ICON: Record<string, ReactNode> = { send: <Send size={14} />, dismiss: <X size={14} /> };
const stamp = (iso: string | null | undefined) => (iso ? new Date(String(iso)).toLocaleString() : "");

export function OwnerInboxLane({
  waiting = [], recent = [], actions = [], counts = { waiting: 0, answered: 0, drafted: 0 },
  draftText = {}, onDraftText, onAct, onOpenMember,
}: {
  waiting?: InboxRow[];
  recent?: InboxRow[];
  actions?: InboxAction[];
  counts?: { waiting: number; answered: number; drafted: number };
  draftText?: Record<string, string>;
  onDraftText?: (id: string, text: string) => void;
  onAct?: (row: InboxRow, action: string, text: string) => void;
  onOpenMember?: (id: string) => void;
}) {
  return (
    <div className="owner-inbox">
      <div className="owner-stats owner-inbox__stats">
        <div className="owner-stat"><strong>{counts.waiting}</strong><span>Waiting on you</span></div>
        <div className="owner-stat"><strong>{counts.answered}</strong><span>Answered for you</span></div>
        <div className="owner-stat"><strong>{counts.drafted}</strong><span>Drafted</span></div>
      </div>

      {!waiting.length ? <p className="owner-page__note">{EMPTY_INBOX_LANE}</p> : null}

      {waiting.map((row) => {
        const text = draftText[row.id] ?? row.draft ?? "";
        return (
          <article key={row.id} className="owner-inbox__item">
            <header>
              {/* D6-4's law, and LR1 ruling 3's: the door rides the opaque ref, and the name shown
                  is the room first-name and nothing else. */}
              <strong>
                {onOpenMember && row.memberId
                  ? <MemberDoor id={row.memberId} name={row.memberName || "a member"} onOpen={onOpenMember} />
                  : (row.memberName || "a member")}
              </strong>
              <span className="owner-lr__kind">{answerClassLabel(row.cls) || row.cls}</span>
              <span className="owner-lr__when">{stamp(row.at)}</span>
            </header>

            {/* THEIR WORDS, VERBATIM. This is the thing she is answering, and a summary of it would
                be the house paraphrasing a member (A1-2's sacred-words law, one surface over). */}
            <p className="owner-inbox__theirs">{row.message || <em>(no text)</em>}</p>

            {/* WHY IT CAME TO HER, in her words rather than the refusal keys the row stores. A key
                with no sentence renders as the KEY, so a refusal added and forgotten is visible here
                instead of reading as no reason at all. */}
            {row.refusals.length ? (
              <p className="owner-inbox__why">{row.refusals.map(refusalSentence).join(" ")}{row.why ? ` ${row.why}` : ""}</p>
            ) : null}

            {row.draft ? (
              <>
                <label className="owner-inbox__label" htmlFor={`inbox-draft-${row.id}`}>Lala's draft. Change any of it before you send.</label>
                <textarea
                  id={`inbox-draft-${row.id}`}
                  className="owner-inbox__draft"
                  value={text}
                  maxLength={AUTO_REPLY_MAX_CHARS}
                  rows={3}
                  onChange={(e) => onDraftText?.(row.id, e.target.value)}
                />
              </>
            ) : (
              /* THE DISTRESS CASE, AND IT SAYS SO PLAINLY. A blank box with no explanation reads as
                 a feature that failed; this reads as the law it is. */
              <p className="owner-inbox__nodraft">{NO_DRAFT_NOTE}</p>
            )}

            <footer>
              {actions.map((a) => (
                <button
                  key={a.key} type="button" className="owner-link" title={a.note}
                  disabled={a.key === "send" && !text.trim()}
                  onClick={() => onAct?.(row, a.key, text)}
                >
                  {ICON[a.key]}{a.label}
                </button>
              ))}
            </footer>
          </article>
        );
      })}

      {/* WHAT WAS ANSWERED FOR HER. Without this the lane only ever shows refusals, and a lane that
          shows nothing while the feature is WORKING reads as a broken one. */}
      {recent.length ? (
        <div className="owner-inbox__recent">
          <h3>Recently</h3>
          {recent.map((row) => (
            <article key={row.id} className="owner-inbox__past">
              <header>
                <strong>{row.memberName || "a member"}</strong>
                <span className="owner-lr__kind">{answerClassLabel(row.cls) || row.cls}</span>
                <span className="owner-lr__when">{stamp(row.sentAt || row.at)}</span>
                {row.sentAt && !row.ownerAction ? <em className="owner-inbox__auto">{SENT_MARK}</em> : null}
              </header>
              <p className="owner-inbox__theirs">{row.message}</p>
              {row.sentBody ? <p className="owner-inbox__sent">{row.sentBody}</p> : null}
              {row.ownerAction === "dismiss" ? <p className="owner-inbox__why">You cleared this. Nothing was sent.</p> : null}
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}
